import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';
import { BaseHttpService } from './base.http.service';
import { Injectable } from '@angular/core';
import { AppConfig } from 'src/app/app.config';
import { SnackBarService } from '../snackbar/snackBar.service';
import { IBankAccountViewModel , IBankAccountResponse, IBankAccountActionResponse as IBankAccountActionResponse, BankAccountStatus } from 'src/app/models/bank-account';
import { IEnvironmentConfig } from 'src/app/models/environmentConfig';
import { Params } from '@angular/router';
import {deduplicatingSwitchMap} from "../../utils/deduplicatingSwitchMap";

@Injectable()
export class BankAccountService extends BaseHttpService {
    constructor(
      private http: HttpClient, 
      private appConfig: AppConfig,
      private snackBarService: SnackBarService) {
        super();
    }
    
    get envSettings(): IEnvironmentConfig {
        return this.appConfig.getEnvironmentConfig();
    }

    /**
     * Retrieves bank accounts for one investor
     * @param investorEntityId
     * @returns 
     */
    public getInvestorBankAccount(investorEntityId: string): Observable<any> {
        const url = `${this.envSettings.webApiUrl}investorEntity/${investorEntityId}/bankaccounts`;
        return this.http.get(url, {
            observe: 'body',
            responseType: 'json',
            headers: this.getHttpHeaders()
        });
    }

    /**
     * Retrieves bank accounts for multiple investors.
     * @param investorEntityIds
     * @returns 
     */
    public getInvestorsBankAccounts(investorEntityIds: number[], statuses: BankAccountStatus[] = [BankAccountStatus.Active]): void {

        if(investorEntityIds.length === 0)
            return;

        var params = {
          investorEntityIds,
          statuses
        } as Params;

         this.http.get(`${this.envSettings.webApiUrl}investorEntities/bankaccounts`.toString(), {
            params: params,
            observe: 'body',
            responseType: 'json',
            headers: this.getHttpHeaders()
        })
        .pipe(
            deduplicatingSwitchMap((data: IBankAccountViewModel []) => {
              return of({
                loading: false,
                data,
                error: false,
              });
            }),
            catchError(() => {
              return of({
                loading: false,
                data: [],
                error: true,
              });
            })
          )
        .subscribe(response => this._investorsBankAccountsResponse$.next(response));
    }

    private _investorsBankAccountsResponse$: BehaviorSubject<IBankAccountResponse> =
    new BehaviorSubject<IBankAccountResponse>({
      loading: false,
      data: [],
      error: false,
    });

    public investorsBankAccountsResponse$: Observable<IBankAccountResponse> = this._investorsBankAccountsResponse$.asObservable();

    public approveBankAccount(investorId: number, bankAccountId: number) : void {
      const url = `${this.envSettings.webApiUrl}investorEntity/${investorId}/bankaccount/${bankAccountId}/approve`;

      this.http.post(url.toString(), {
          observe: 'body',
          responseType: 'json',
          headers: this.getHttpHeaders()
      })
      .pipe(
          map(() => {
            this.snackBarService.showSnackBar("Successfully approved bank account.");
            return {
              loading: false,
              error: false,
            };
          }),
          catchError(() => {
            this.snackBarService.showWarningSnackBar("Failed to approve bank account. Please try again.");
            return of({
              loading: false,
              error: true,
            });
          })
        )
        .subscribe(response => this._bankAccountActionResponse$.next(response));
    }

    public rejectBankAccount(investorId: number, bankAccountId: number) : void {
      const url = `${this.envSettings.webApiUrl}investorEntity/${investorId}/bankaccount/${bankAccountId}/reject`;

      this.http.post(url.toString(), {
          observe: 'body',
          responseType: 'json',
          headers: this.getHttpHeaders()
      })
      .pipe(
          map(() => {
            this.snackBarService.showSnackBar("Successfully rejected bank account.");
            return {
              loading: false,
              error: false,
            };
          }),
          catchError(() => {
            this.snackBarService.showWarningSnackBar("Failed to reject bank account. Please try again.");
            return of({
              loading: false,
              error: true,
            });
          })
        )
        .subscribe(response => this._bankAccountActionResponse$.next(response));
    }

    private _bankAccountActionResponse$: BehaviorSubject<IBankAccountActionResponse> =
    new BehaviorSubject<IBankAccountActionResponse>({
      loading: true,
      error: false,
    });

    public bankAccountActionResponse$: Observable<IBankAccountActionResponse> = this._bankAccountActionResponse$.asObservable();
}